import { action, observable } from 'mobx';

type APIDataToSend = (number | string | object)[];

/**
 * @class HelperStore
 * @type Store
 * @description Creates a mobx store for each API call. Each API calls loading,
 *              data and error information will be stored here.
 */
class HelperStore {
  /**
   * @field loading
   * @description Indicates whether the API call has completed.
   */
  @observable loading = false;

  /**
   * @field errorMessage
   * @description The error message received from the API call.
   */
  @observable errorMessage = '';

  /**
   * @field dataReceived
   * @description The data received from the API call.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @observable dataReceived: any;

  private serviceFunction: Function;

  private dataToSend: APIDataToSend;

  private defaultReceivedValue: object;

  constructor(serviceFunction: Function, dataToSend: APIDataToSend, defaultReceivedValue: object) {
    this.serviceFunction = serviceFunction;
    this.dataToSend = dataToSend;
    this.defaultReceivedValue = defaultReceivedValue;
  }

  /**
   * @method execute
   * @description Invokes the service method and sets the data received from the API call.
   */
  @action
  async execute(): Promise<void> {
    this.errorMessage = '';
    this.loading = true;
    try {
      this.dataReceived = await this.serviceFunction.apply(null, this.dataToSend);
      this.loading = false;
    } catch (e) {
      this.dataReceived = this.defaultReceivedValue;
      if (typeof e === 'string') {
        this.errorMessage = e;
      }
      this.loading = false;
    }
  }

  /**
   * @method reset
   * @description Reset the store data to initial state.
   */
  @action.bound
  reset(): void {
    this.errorMessage = '';
    this.loading = false;
    this.dataReceived = null;
  }

  /**
   * @set dataToBeSend
   * @param data : APIDataToSend
   * @description To set dynamic data in this.dataToSend property
   */
  set setDataToSend(data: APIDataToSend) {
    this.dataToSend = data;
  }
}

export default HelperStore;
